import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material'
import { FunctionComponent, SetStateAction, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FileUploadHelper } from '../../../miscellaneous-components/file-upload-helper';
import { DialogSaveButtons } from '../../../miscellaneous-components/dialog-save-buttons';
import CustomFieldService from '../../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../../shared/services/custom-field/custom-field.model';
import { EmissionsFilterLogic, IUnitChoice } from '../emissions-filter/emissions-filter-logic';
import { IReportAction } from '../../../../../shared/services/report-action/report-action.model';
import SubsidiaryService from '../../../../../shared/services/subsidiary/subsidiary.service';
import { UserDetailsContext } from '../../../../../context/user-details/user-details';
import { ISubsidiary } from '../../../../../shared/services/subsidiary/subsidiary.model';
import { EmissionsInputAdvanced } from '../emissions-input/emissions-input-advanced-form';
import moment from 'moment';
import TransactionService from '../../../../../shared/services/transaction/transaction.service';
import ReportActionService from '../../../../../shared/services/report-action/report-action.service';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import { formatTransaction } from '../../../../../shared/utils/format-post-utils';
import ConversionSourceService from '../../../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../../../shared/services/conversion-source/conversion-source.model';
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked';
import { ScreenLabelsContext } from '../../../../../context/screen-labels/screen-labels';
import { getPreferredSubsidiary, getSelectableSubsidiaries } from '../../../../../shared/utils/subsidiary-utils';
import { isEtsGroupId } from '../../../../../shared/utils/group-ids-utils';

export const EmissionsActionInputGeneral: FunctionComponent<{
  categoryId: string,
  reportAction: IReportAction
  setViewActions: React.Dispatch<SetStateAction<any>>,
  getGroup: () => void,
  groupId: string,
  scope: string,
}> = ({
  categoryId,
  reportAction,
  setViewActions,
  getGroup,
  groupId,
  scope,
}) => {
    //Services
    const conversionSourceService = new ConversionSourceService();
    const customFieldService = new CustomFieldService()
    const subsidiaryService = SubsidiaryService.getInstance();
    const transactionService = new TransactionService()
    const userDetailsContext = useContext(UserDetailsContext);
    const userDetails = userDetailsContext.userDetails;
    const userNotificationsContext = useContext(UserNotificationsContext);
    //State
    const [defaultConversionSourceId, setDefaultConversionSourceId] = useState<string>();
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>();
    const [unitChoice, setUnitChoice] = useState<IUnitChoice | null>(null);
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>();
    const [customFields, setCustomFields] = useState<ICustomField[]>();
    const [fileData, setFileData] = useState([]);
    const [calendarYear, setCalendarYear] = useState<string>();
    const [vehicleConversionPreSeeded, setVehicleConversionPreSeeded] = useState(false);
    const [priorValue, setPriorValue] = useState<{ quantity: number, unitName: string | null, periodLabel: string } | null>(null);
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //Loading State
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    // The prior period's value for the same entity, shown as a sanity hint under the amount so
    // an obviously wrong entry is obvious before it reaches a report. Best-effort: absent when
    // there is no comparable earlier entry.
    useEffect(() => {
      new ReportActionService().getPriorValue(reportAction.id)
        .then(response => {
          if (response.available && response.quantity != null) {
            setPriorValue({ quantity: response.quantity, unitName: response.unitName || null, periodLabel: response.periodLabel || 'Last period' })
          } else {
            setPriorValue(null)
          }
        })
        .catch(() => setPriorValue(null))
    }, [reportAction.id])
    //Formik
    const transactionSchema = Yup.object().shape({
      subsidiaryId: Yup.string().required(`You must provide a ${screenLabelsContext.getLabel('subsidiary', { plural: false })}`),
      countryId: Yup.string().required('You must provide a country'),
      returnId: Yup.string().required('You must provide a return'),
      measurementUnitId: Yup.string().required('You must provide units'),
      unitsConsumed: Yup.number().min(0.01, 'The amount must be greater than 0').required('You must provide an amount'),
      customMarketBasedMultiplier: Yup.number().nullable(),
      energySupplierTariffId: Yup.string().nullable(),
      partOfEts: Yup.boolean(),
      regulatedEmissionTradingSchemeId: Yup.string().when('partOfEts', {
        is: true,
        then: schema => schema.required('You need to select a regulated emission trading scheme'),
        otherwise: schema => schema.nullable().notRequired()
      }),
      projectId: Yup.string().nullable(),
      contactSupplierId: Yup.string().nullable(),
      locationId: Yup.string().nullable(),
      vehicleId: Yup.string().nullable(),
      capitalGoodId: Yup.string().nullable(),
      renewableEnergyCertificateIds: Yup.array().of(Yup.string()).nullable(),
      excludeFromReporting: Yup.boolean(),
      excludeFromReportingReason: Yup.string().when('excludeFromReporting', {
        is: true,
        then: (schema) => schema.required('You must provide a reason for exclusion'),
        otherwise: (schema) => schema.nullable().notRequired(),
      }),
    }).test(
      'tariff-or-custom-xor',
      'Choose either an Energy Supplier Tariff or provide a custom Supplier Emission Rate (not both)',
      function (value) {
        const v: any = value || {}
        const hasTariff = !!v.energySupplierTariffId
        const hasCustom = v.customMarketBasedMultiplier !== null && v.customMarketBasedMultiplier !== '' && v.customMarketBasedMultiplier !== undefined
        return !(hasTariff && hasCustom)
      }
    );
    const formik = useFormik<any>({
      initialValues: {
        subsidiaryId: '',
        countryId: '',
        unitsConsumed: undefined,
        measurementUnitId: '',
        returnId: '',
        customMarketBasedMultiplier: undefined,
        partOfEts: false,
        regulatedEmissionTradingSchemeId: null,
        projectId: null,
        contactSupplierId: null,
        locationId: null,
        vehicleId: null,
        capitalGoodId: null,
        renewableEnergyCertificateIds: [],
        excludeFromReporting: false,
        excludeFromReportingReason: '',
      },
      validationSchema: transactionSchema,
      onSubmit: (values) => {
        values.relatedConversionModelName = 'conversion'
        values.reportPeriodId = reportAction.reportPeriodId;
        values.startDate = reportAction.startDate;
        values.endDate = reportAction.endDate;
        values.dataType = 'Actual Data'
        values.groupId = groupId;
        values.categoryId = categoryId;
        values.locationId = reportAction.location?.id;
        values.vehicleId = reportAction.vehicle?.id;
        const response = formatTransaction(values, groupId, true, customFields, categoryId)
        const form: any = new FormData();
        for (let i = 0; i < fileData.length; i++) {
          form.append("files", fileData[i]);
        }
        form.append('transaction', JSON.stringify(response));
        form.append('reportActionId', JSON.stringify(reportAction.id));
        if (isReportPeriodLocked) {
          userNotificationsContext.openDialog({
            active: true,
            title: 'Action Not Allowed',
            message: `The selected report period is locked and cannot be modified`,
            buttonCopy: 'Ok',
            hideCancelButton: true
          })
        } else {
          if (!isSubmitting) {
            setIsSubmitting(true)
            transactionService.postTransaction(form)
              .then(() => {
                setViewActions('actions')
                getGroup()
                setIsSubmitting(false)
                userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
              })
              .catch(err => {
                if (err.response.data.code === 1) {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Report period is locked', backgroundColor: 'text.primary' });
                } else {
                  const message = err?.response?.data?.message || 'There was an error, please try again'
                  userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' });
                }
                setIsSubmitting(false)
                console.log(err)
              })
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'Saving in progress, please wait' });
          }
        }
      }
    });

    //Use Effects
    useEffect(() => {
      conversionSourceService.getConversionSourceForCategory(categoryId, 'conversion', scope, true)
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => {
          console.log(err)
        })
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => {
          console.log(err)
        })
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, []);

    useEffect(() => {
      if (reportAction?.startDate) {
        setCalendarYear(moment(reportAction.startDate).format('YYYY'))
      }
    }, [reportAction?.startDate])

    useEffect(() => {
      if (userDetails && subsidiaries && conversionSources) {
        if (reportAction.vehicle) {
          const vehicle = reportAction.vehicle;
          const vehicleSubsidiaryId = vehicle.subsidiaryId;
          const defaultSubsidiary = subsidiaries.find(s => s.id === vehicleSubsidiaryId);
          if (defaultSubsidiary) {
            formik.setFieldValue('countryId', defaultSubsidiary.countryId);
            formik.setFieldValue('subsidiaryId', defaultSubsidiary.id);
          }
          // Prefer vehicle's own conversion source if it has one
          const vehicleConversionSourceId = (vehicle as any).conversionSourceId;
          const vehicleConversion = (vehicle as any).conversion;
          const vehicleConversionId = (vehicle as any).conversionId;
          if (vehicleConversionSourceId && conversionSources.find(s => s.id === vehicleConversionSourceId)) {
            setDefaultConversionSourceId(vehicleConversionSourceId);
            if (vehicleConversion && vehicleConversionId) {
              formik.setFieldValue('detailLevelOne', vehicleConversion.detailLevelOne || '');
              formik.setFieldValue('detailLevelTwo', vehicleConversion.detailLevelTwo || '');
              formik.setFieldValue('detailLevelThree', vehicleConversion.detailLevelThree || '');
              formik.setFieldValue('detailLevelFour', vehicleConversion.detailLevelFour || '');
              formik.setFieldValue('detailLevelFive', vehicleConversion.detailLevelFive || '');
              formik.setFieldValue('returnId', vehicleConversionId);
              setVehicleConversionPreSeeded(true);
            }
          } else if (defaultSubsidiary) {
            setDefaultConversionSourceId(defaultSubsidiary.defaultConversionSourceId);
          }
        } else if (reportAction.location) {
          const locationSubsidiaryId = reportAction.location.subsidiaryId
          const defaultSubsidiary = subsidiaries.find(s => s.id === locationSubsidiaryId)
          if (defaultSubsidiary) {
            setDefaultConversionSourceId(defaultSubsidiary.defaultConversionSourceId)
            formik.setFieldValue('countryId', defaultSubsidiary.countryId)
            formik.setFieldValue('subsidiaryId', defaultSubsidiary.id)
          } else {
            const preferred = getPreferredSubsidiary(subsidiaries, userDetails)
            if (preferred) {
              formik.setFieldValue('subsidiaryId', preferred.id)
              formik.setFieldValue('countryId', preferred.countryId)
              const defaultConversionSourceId = preferred.defaultConversionSourceId
              if (conversionSources?.find(conversionSource => conversionSource.id === defaultConversionSourceId)) {
                setDefaultConversionSourceId(defaultConversionSourceId)
              } else {
                setDefaultConversionSourceId(undefined)
              }
            }
          }
        } else {
          const preferred = getPreferredSubsidiary(subsidiaries, userDetails)
          if (preferred) {
            formik.setFieldValue('subsidiaryId', preferred.id)
            formik.setFieldValue('countryId', preferred.countryId)
            const defaultConversionSourceId = preferred.defaultConversionSourceId
            if (conversionSources?.find(conversionSource => conversionSource.id === defaultConversionSourceId)) {
              setDefaultConversionSourceId(defaultConversionSourceId)
            } else {
              setDefaultConversionSourceId(undefined)
            }
          }
        }
      }
    }, [userDetails, subsidiaries, conversionSources])

    useEffect(() => {
      if (reportAction.location && reportAction.location.customMarketBasedMultiplier) {
        formik.setFieldValue('customMarketBasedMultiplier', reportAction.location.customMarketBasedMultiplier)
      }
    }, [reportAction])

    //Formik Event Handlers
    const conversionSourceHandler = (event: SelectChangeEvent<string>) => {
      const conversionSource = conversionSources?.find(conversionSource => conversionSource.id === event.target.value)
      if (conversionSource) {
        setDefaultConversionSourceId(conversionSource.id)
        // Clear pre-seeded vehicle conversion so EmissionsFilterLogic resets
        if (vehicleConversionPreSeeded) {
          formik.setFieldValue('detailLevelOne', '');
          formik.setFieldValue('detailLevelTwo', '');
          formik.setFieldValue('detailLevelThree', '');
          formik.setFieldValue('detailLevelFour', '');
          formik.setFieldValue('detailLevelFive', '');
          formik.setFieldValue('returnId', '');
          setVehicleConversionPreSeeded(false);
        }
      }
    }

    const subsidiaryHandler = (event: SelectChangeEvent<string>) => {
      const subsidiary = subsidiaries?.find(subsidiary => subsidiary.id === event.target.value)
      if (subsidiary) {
        formik.setFieldValue('subsidiaryId', subsidiary.id)
        formik.setFieldValue('countryId', subsidiary.countryId)
        const subsidiaryDefaultConversionSource = conversionSources?.find(conversionSource => conversionSource.id === subsidiary.defaultConversionSourceId)
        if (subsidiaryDefaultConversionSource) {
          setDefaultConversionSourceId(subsidiaryDefaultConversionSource.id)
        }
      }
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Box component='form' onSubmit={formik.handleSubmit}>
          {(reportAction.actionCode === 'location_electricity' || reportAction.actionCode === 'location_gas' || reportAction.actionCode === 'location_aircon' || reportAction.actionCode === 'location_steam') &&
            <>
              {reportAction.actionBillingPeriod === 'monthly' && reportAction.location && <Typography variant='h6' sx={{ color: 'text.primary', pb: 1 }}> What was the total {reportAction.actionCode === 'location_gas' && 'gas'} {reportAction.actionCode === 'location_electricity' && 'electricity'} {reportAction.actionCode === 'location_aircon' && 'air conditioning'} usage for {reportAction.location.friendlyName} in {reportAction.actionPeriod} {reportAction.actionYear}?</Typography>}
              {reportAction.actionBillingPeriod === 'annually' && reportAction.location && <Typography variant='h6' sx={{ color: 'text.primary', pb: 1 }}> What was the total {reportAction.actionCode === 'location_gas' && 'gas'} {reportAction.actionCode === 'location_electricity' && 'electricity'} {reportAction.actionCode === 'location_aircon' && 'air conditioning'} usage for {reportAction.location.friendlyName} in {`${moment(reportAction.startDate).year()}-${moment(reportAction.endDate).year()}`}?</Typography>}
            </>
          }
          {reportAction.actionCode === 'vehicle' && reportAction.vehicle &&
            <>
              {reportAction.actionBillingPeriod === 'annually' && <Typography variant='h6' sx={{ color: 'text.primary' }}> What was the total usage for Vehicle {reportAction.vehicle.customUniqueId} in {`${moment(reportAction.startDate).year()}-${moment(reportAction.endDate).year()}`}?</Typography>}
              {reportAction.actionBillingPeriod === 'monthly' && <Typography variant='h6' sx={{ color: 'text.primary' }}>What was the total distance travelled for Vehicle {reportAction.vehicle.customUniqueId} in {reportAction.actionPeriod} {reportAction.actionYear}?</Typography>}
            </>
          }
          <Box sx={{ mr: 2 }}>
            {subsidiaries && (userDetails?.organisation?.enableSubsidiaries || subsidiaries.length > 1) &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })}</InputLabel>
                <Select
                  id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { capitalize: 'first' })} name='subsidiaryId'
                  value={formik.values.subsidiaryId || ''}
                  onChange={subsidiaryHandler}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.pprimary', }}>
                  {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                    <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                  )}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                  </FormHelperText>
                }
              </FormControl>
            }
            {conversionSources &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }} >
                <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
                <Select id='conversionSourceId' name='conversionSourceId' label='Conversion Factor Source'
                  value={defaultConversionSourceId || ''}
                  onChange={conversionSourceHandler}>
                  {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) => (
                    <MenuItem key={index} value={conversionSource.id}>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <span>{conversionSource.source}</span>
                        {conversionSource.country && (
                          <img
                            loading="lazy"
                            width="20"
                            src={conversionSource.country.imageUrl}
                            alt={`${conversionSource.country.name} flag`}
                          />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>}
            {calendarYear && categoryId && groupId && defaultConversionSourceId ?
              <Stack sx={{ mt: 2 }}>
                <EmissionsFilterLogic
                  categoryId={categoryId}
                  groupId={groupId}
                  defaultConversionSourceId={defaultConversionSourceId}
                  formik={formik}
                  calendarYear={calendarYear}
                  isEdit={vehicleConversionPreSeeded}
                  externalUnitSelect
                  onUnitChoiceChange={setUnitChoice}
                  direction='row' />
              </Stack> :
              <Typography variant='body2' sx={{ color: 'text.secondary', mt: 2 }}>
                Choose a conversion factor source and the matching emission factors will appear.
              </Typography>
            }
            <Grid>
              {/* Amount and its unit read as one measurement, the same as manual entry. The unit
                  is always present so the row does not reflow when a factor is chosen: the
                  picker fills its options in and enables it once the activity has units. */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                <FormControl fullWidth variant='outlined' sx={{ flex: { sm: 4 } }}>
                  <InputLabel htmlFor='unitsConsumed'>Amount</InputLabel>
                  <OutlinedInput inputProps={{ min: 0.01, step: 0.01 }} id='unitsConsumed' label='amount' name='unitsConsumed' type='number'
                    value={formik.values.unitsConsumed} onChange={formik.handleChange}
                    error={formik.touched.unitsConsumed && Boolean(formik.errors.unitsConsumed)}
                    sx={{ color: 'text.primary' }}></OutlinedInput>
                  {formik.touched.unitsConsumed && formik.errors.unitsConsumed &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.unitsConsumed ? <div>{formik.errors.unitsConsumed as string}</div> : null}
                    </FormHelperText>
                  }
                  {priorValue && !(formik.touched.unitsConsumed && formik.errors.unitsConsumed) && (() => {
                    // Warn, never block: a genuine 4x jump must still be enterable.
                    const enteredAmount = Number(formik.values.unitsConsumed)
                    const implausibleEntry = priorValue.quantity > 0
                      && Number.isFinite(enteredAmount) && enteredAmount > 0
                      && (enteredAmount / priorValue.quantity >= 3 || enteredAmount / priorValue.quantity <= 1 / 3)
                    return (
                      <FormHelperText sx={{ marginLeft: '16px !important', color: implausibleEntry ? 'warning.dark' : 'text.secondary' }}>
                        {`${priorValue.periodLabel}: ${priorValue.quantity.toLocaleString('en-GB', { maximumFractionDigits: 2 })}${priorValue.unitName ? ` ${priorValue.unitName}` : ''}`}
                        {implausibleEntry ? '. This entry is a long way from that figure. Double-check it; a genuine change is fine to save.' : ''}
                      </FormHelperText>
                    )
                  })()}
                </FormControl>
                <FormControl fullWidth variant='outlined' sx={{ flex: { sm: 1 } }} disabled={!unitChoice || unitChoice.disabled}>
                  <InputLabel htmlFor='measurementUnitName'>Unit</InputLabel>
                  <Select
                    id='measurementUnitName'
                    label='Unit'
                    value={unitChoice?.value ?? ''}
                    onOpen={unitChoice?.onOpen}
                    onChange={(event) => unitChoice?.onChange(event.target.value as string)}
                    error={formik.touched.measurementUnitId && Boolean(formik.errors.measurementUnitId)}
                    sx={{ color: 'text.primary' }}>
                    {(unitChoice?.options ?? []).map(unit => (
                      <MenuItem key={unit.id} value={unit.name}>{unit.name}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.measurementUnitId && formik.errors.measurementUnitId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors.measurementUnitId as string}
                    </FormHelperText>
                  }
                </FormControl>
              </Stack>
            </Grid>
            {customFields &&
              <EmissionsInputAdvanced
                customFields={customFields}
                formik={formik}
                hideLocation={reportAction.location ? true : false}
                hideVehicle={reportAction.vehicle ? true : false}
                hideRegulatedEmissionTradingScheme={!isEtsGroupId(groupId)}
                hideRec={reportAction.actionCode !== 'location_electricity'}
                hideCustomBasedMarketMultiplier={reportAction.actionCode !== 'location_electricity'}
                defaultContactSupplierId={
                  reportAction.location ? (
                    reportAction.actionCode === 'location_electricity' ? reportAction.location.electricitySupplierId :
                      reportAction.actionCode === 'location_steam' ? reportAction.location.heatAndSteamSupplierId :
                        reportAction.actionCode === 'location_gas' ? reportAction.location.gasSupplierId :
                          reportAction.actionCode === 'location_aircon' ? reportAction.location.airConSupplierId :
                            undefined
                  ) : undefined
                }
                defaultEnergySupplierTariffId={
                  reportAction.actionCode === 'location_electricity' && reportAction.location ? reportAction.location.energySupplierTariffId : undefined
                }
              />}
            <Grid>
              <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
                <FileUploadHelper fileData={fileData} setFileData={setFileData} disableButton={isSubmitting} />
                <DialogSaveButtons
                  isForm={true}
                  isSubmitting={isSubmitting}
                  hideCancelButton={true}
                  disableSaveButton={!formik.values.returnId}
                  disabledReason='Finish choosing an emission factor above to save this record'
                  submitHandler={formik.submitForm}
                />
              </Stack>
            </Grid>
          </Box>
        </Box>
      </Box >
    )
  }